import React from "react";
import RequireAuthGuard from "./components/auth/RouteGard";
import { createBrowserRouter, Navigate } from "react-router-dom";
import ConferenceLayout from "./layout/conference-layout/conference-layout";
import GrievanceLayout from "./layout/grievance-layout/grievance-layout";
import SupplierRegistrationLayout from "./layout/supplier-registration-layout/supplier-layout";
import NotFoundPage from "./components/not-found";
import QualitativeAssessmentDashboard from "./layout/QualitativeAssessmentDashboard/QualitativeAssessment";
import ManageQualitativeAssessmentForm from "./layout/QualitativeAssessmentDashboard/managePage";
import CreateQualitativeAssessmentForm from "./layout/QualitativeAssessmentDashboard/createForm";
import ViewData from "./layout/QualitativeAssessmentDashboard/ViewData";
import { ErrorBoundary } from "./components/error-boundary/error-boundary";

const EmailVerification = React.lazy(
  () => import("./components/email-verification"),
);
const SystemChat = React.lazy(() => import("./components/Chat/SystemChat"));
const ScopeOfPrograms = React.lazy(
  () => import("./layout/qualitative-assessment-latest/program-scope"),
);
const GrievancePortal = React.lazy(() => import("./layout/grievance-portal"));
// Lazy load the components
const HIPDActivities = React.lazy(
  () =>
    import("./layout/GlobalOperations/Activities/HIPD-activities/HIPDActivities"),
);
const GDActivities = React.lazy(
  () =>
    import("./layout/GlobalOperations/Activities/GD-activities/GDActivities"),
);
const ProgramActivities = React.lazy(
  () =>
    import("./layout/GlobalOperations/Activities/ProgramActivities/ProgramActivities"),
);
const HIPD = React.lazy(() => import("./layout/hipdEntered/hipdEntered"));
const FetchFeedBack = React.lazy(() => import("./layout/feedback"));
const Home = React.lazy(() => import("./layout/home/home"));
const Dashboard = React.lazy(() => import("./layout/dashboard/dashboard"));
const OverseasActivities = React.lazy(
  () => import("./layout/OverseasesEntry/OverseasActivities"),
);
const Earth = React.lazy(() => import("./layout/overview/overview"));
const GlobalOperations = React.lazy(
  () =>
    import("./layout/GlobalOperations/ResultChain/GlobalOperationsActivities"),
);
const ChatApp = React.lazy(() => import("./layout/MhgAI/MhgAI"));
const Complains = React.lazy(() => import("./layout/complains/complains"));
const MonitoringActionTracker = React.lazy(
  () => import("./layout/MonitoringActionTracker/MonitoringActionTracker"),
);
const Governance = React.lazy(() => import("./layout/Governance/Governance"));
const OverseasActivitiesReport = React.lazy(
  () => import("./layout/OverseasActivitiesReport/overseasactivitiesreport"),
);
// const HIPD = React.lazy(() => import("./layout/hipdEntered/hipdEntered"));
const MHEMS = React.lazy(() => import("./layout/Mhems/mhems"));
const Directory = React.lazy(() => import("./layout/Directory/directory"));
const Activation = React.lazy(() => import("./layout/Account/Activation"));
const ConferenceOverview = React.lazy(
  () => import("./layout/ConferenceOverview/index.jsx"),
);
const Recovery = React.lazy(() => import("./layout/Account/Recovery"));
const RecoveryConfirmation = React.lazy(
  () => import("./layout/Account/RecoveryConfirmation"),
);
const Profile = React.lazy(() => import("./layout/Account/Profile"));
const ProjectLog = React.lazy(() => import("./layout/ProjectLog/ProjectLog"));
const RiskRegister = React.lazy(
  () => import("./layout/RiskRegisterReport/RiskRegisterReport"),
);
const MealPlanning = React.lazy(
  () => import("./layout/meal-planning/index.jsx"),
);
const FinancialFeedback = React.lazy(
  () => import("./layout/FinancialFeedback/index.jsx"),
);
const OSDashboard = React.lazy(() => import("./layout/OSDashboard/index.jsx"));
const CreateBeneficiary = React.lazy(
  () => import("./layout/OSDashboard/pages/StepperForm/index.jsx"),
);
const CreateMealPlan = React.lazy(
  () => import("./layout/meal-planning/CreateMealPlan.jsx"),
);
const UpdateMealPlan = React.lazy(
  () => import("./layout/meal-planning/UpdateMealPlan.jsx"),
);
const QualitativeAssessments = React.lazy(
  () => import("./layout/QualitativeAssessments/QualitativeAssessment"),
);
const QualitativeAssessmentLatest = React.lazy(
  () => import("./layout/qualitative-assessment-latest"),
);

const ManageQAssessments = React.lazy(
  () => import("./layout/QualitativeAssessments/listQA"),
);
const SMTDashboard = React.lazy(
  () => import("./layout/SMTDashboard/SMTDashboard"),
);
const GlobalResultsFramework = React.lazy(
  () => import("./layout/GlobalResultsFramework/GlobalResultsFramework"),
);
const DepartmentActivities = React.lazy(
  () => import("./layout/GlobalOperations/Activities/DepartmentActivities"),
);
const PMTDashboard = React.lazy(() => import("./layout/PMTDashboard"));
const CallForProposal = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/call-for-proposal/call-for-proposal"),
);
const CreateConceptNote = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/create-concept-note/create-concept-note"),
);
const AuthLayout = React.lazy(() => import("./layout/auth-layout/auth-layout"));
const SalesLayout = React.lazy(
  () => import("./layout/sales-layout/sales-layout.jsx"),
);
const ProjectDetails = React.lazy(
  () => import("./layout/PMTDashboard/pages/general-pages/proposal-details"),
);
const FranceResultsFramework = React.lazy(
  () => import("./layout/FranceResultsFramework/FranceResultsFramework"),
);

const CreateProposal = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/create-proposal/create-proposal"),
);
const FranceFrameWork = React.lazy(() => import("./layout/france-framework"));
const ScoringTemplate = React.lazy(
  () => import("./layout/PMTDashboard/pages/tabs-pages/scoring-template"),
);
const FundingAgreement = React.lazy(
  () => import("./layout/PMTDashboard/pages/tabs-pages/funding-agreement"),
);
const HumanResourceEdit = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/human-resource-edit"),
);
const ProcurementPlan = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/procurement-plan"),
);
const AssetRegister = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/asset-register"),
);

const RiskRegisterDetails = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/risk-register"),
);

const MealReporting = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/meal-reporting"),
);

const ChangeRequestForm = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/change-request-form"),
);

const FinancialDetails = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/financial-details"),
);

const ReportMisConduct = React.lazy(() => import("./layout/report-misconduct"));

const PmtReporting = React.lazy(
  () =>
    import("./layout/PMTDashboard/pages/general-pages/proposal-details/pmt-reporting"),
);

const SalesSite = React.lazy(() => import("./layout/sales-site"));

const OrgHealthLayout = React.lazy(
  () => import("./layout/org-health-app-layout/health-app-layout"),
);

// const DueDiligence = React.lazy(
//   () => import("./layout/org-health-app/due-diligence"),
// );

const ProjectManagement = React.lazy(
  () => import("./layout/org-health-app/project-management"),
);

const OrganizationManagement = React.lazy(
  () => import("./layout/org-health-app/organization-management"),
);

const HumanResourceManagement = React.lazy(
  () => import("./layout/org-health-app/human-resource-management"),
);

const FinancialManagement = React.lazy(
  () => import("./layout/org-health-app/financial-management"),
);

const Administration = React.lazy(
  () => import("./layout/org-health-app/administration"),
);

const GovernanceManagement = React.lazy(
  () => import("./layout/org-health-app/governance"),
);

const ComparisonPage = React.lazy(
  () => import("./layout/org-health-app/comparison"),
);

const OverallComparisonPage = React.lazy(
  () => import("./layout/org-health-app/overall-comparison"),
);

const WeeklyPlanner = React.lazy(() => import("./layout/planner"));
const FeedbackDetails = React.lazy(() => import("./layout/feedback-details"));
const MediaManagement = React.lazy(() => import("./layout/media-management"));
const MediaDetails = React.lazy(
  () => import("./layout/media-management/media-details"),
);
const MicroGrant = React.lazy(() => import("./layout/grants/micro-grant"));
const MicroGrantForm = React.lazy(
  () => import("./layout/grants/micro-grant/form"),
);
const SupplierRegistration = React.lazy(
  () => import("./layout/supplier-registration"),
);
const SupplierWelcomePage = React.lazy(
  () => import("./layout/supplier-registration/welcome-page"),
);
const PurchaseRequisition = React.lazy(
  () => import("./layout/purchase-requisition"),
);

const ProcurementLayout = React.lazy(
  () => import("./layout/procurement-layout/procurement-layout"),
);
const ProcurementOverview = React.lazy(
  () => import("./layout/procurement-dashboard/overview"),
);

const SupplierRegistrationDashboard = React.lazy(
  () => import("./layout/procurement-dashboard/supplier-registration"),
);
const PurchaseRequisitionDashboard = React.lazy(
  () => import("./layout/procurement-dashboard/purchase-requisition"),
);

const BMIS = React.lazy(() => import("./layout/bmis"));

export const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <ErrorBoundary />,
    children: [
      // Public routes - Grievance section
      {
        element: <GrievanceLayout />,
        children: [
          {
            path: "grievance-form",
            element: <GrievancePortal />,
          },
          {
            path: "verify-email",
            element: <EmailVerification />,
          },
        ],
      },
      // Public routes - Sales section
      {
        element: <SalesLayout />,
        children: [
          {
            index: true,
            element: <SalesSite />,
          },
        ],
      },
      // Public routes - Supplier Registration (NO AUTH REQUIRED)
      // This is the EXTERNAL vendor-facing form. Kept exactly as-is.
      {
        element: <SupplierRegistrationLayout />,
        children: [
          {
            path: "procurement/supplier-registration",
            element: <SupplierRegistration />,
          },
          {
            path: "procurement/supplier-welcome",
            element: <SupplierWelcomePage />,
          },
        ],
      },
      // Public routes - Authentication section
      {
        element: <AuthLayout />,
        children: [
          {
            path: "sign-in",
            element: <Home />,
          },
          {
            path: "forget-password",
            element: <Recovery />,
          },
          {
            path: "recovery-confirmation",
            element: <RecoveryConfirmation />,
          },
          {
            path: "confirmation",
            element: <Activation />,
          },
        ],
      },
      // Authenticated routes - Conference section
      {
        element: (
          <RequireAuthGuard>
            <ConferenceLayout />
          </RequireAuthGuard>
        ),
        children: [
          {
            path: "conference-overview",
            element: <ConferenceOverview />,
          },
        ],
      },

      // Authenticated routes - Dashboard section
      {
        element: (
          <RequireAuthGuard>
            <Dashboard />
          </RequireAuthGuard>
        ),
        children: [
          {
            path: "pmt-dashboard",
            element: <PMTDashboard />,
            children: [
              {
                path: "proposal-discussion",
                element: <ProjectDetails />,
              },
              {
                path: "call-for-proposal",
                element: <CallForProposal />,
              },
              {
                path: "create-concept-note",
                element: <CreateConceptNote />,
              },
              {
                path: "create-proposal",
                element: <CreateProposal />,
              },
              {
                path: "scoring-template",
                element: <ScoringTemplate />,
              },
              {
                path: "funding-agreement",
                element: <FundingAgreement />,
              },
              {
                path: "asset-register",
                element: <AssetRegister />,
              },
              {
                path: "risk-register",
                element: <RiskRegisterDetails />,
              },
              {
                path: "meal-reporting",
                element: <MealReporting />,
              },
              {
                path: "human-resource",
                element: <HumanResourceEdit />,
              },
              {
                path: "procurement-plan",
                element: <ProcurementPlan />,
              },
              {
                path: "change-request-form",
                element: <ChangeRequestForm />,
              },
              {
                path: "pmt-reporting",
                element: <PmtReporting />,
              },
              {
                path: "financial-details",
                element: <FinancialDetails />,
              },
            ],
          },
          {
            path: "overview",
            element: <Earth />,
          },
          {
            path: "overseas-activities",
            element: <OverseasActivities />,
          },
          {
            path: "planner",
            element: <WeeklyPlanner />,
          },
          {
            path: "feedback-details",
            element: <FeedbackDetails />,
          },
          {
            path: "media-management",
            element: <MediaManagement />,
          },
          {
            path: "media-management/details",
            element: <MediaDetails />,
          },
          {
            path: "grants/micro-grant",
            element: <MicroGrant />,
          },
          {
            path: "grants/micro-grant/new",
            element: <MicroGrantForm />,
          },
          {
            path: "grants/micro-grant/:id/view",
            element: <MicroGrantForm mode="view" />,
          },
          {
            path: "grants/micro-grant/:id/edit",
            element: <MicroGrantForm mode="edit" />,
          },
          {
            path: "qualitative-assessment-dashboard",
            element: <QualitativeAssessmentDashboard />,
            children: [
              {
                path: "create-qa-form",
                element: <CreateQualitativeAssessmentForm />,
              },
              {
                path: "manage-page",
                element: <ManageQualitativeAssessmentForm />,
              },
              {
                path: "view-data",
                element: <ViewData />,
              },
            ],
          },
          {
            path: "grievance-portal",
            element: <ReportMisConduct />,
          },
          {
            path: "os-report",
            element: <OverseasActivitiesReport />,
          },
          {
            path: "generative-ai",
            element: <ChatApp />,
          },
          {
            path: "complains",
            element: <Complains />,
          },
          {
            path: "pmf-tracker",
            element: <MonitoringActionTracker />,
          },
          {
            path: "governance",
            element: <Governance />,
          },
          {
            path: "hipd",
            element: <HIPD />,
          },
          {
            path: "meetings",
            element: <MHEMS />,
          },
          {
            path: "directory",
            element: <Directory />,
          },
          {
            path: "project-logs",
            element: <ProjectLog />,
          },
          {
            path: "profile",
            element: <Profile />,
          },
          {
            path: "risk-register",
            element: <RiskRegister />,
          },
          {
            path: "conversation",
            element: <SystemChat />,
          },
          {
            path: "meal-planning",
            element: <MealPlanning />,
            children: [
              {
                path: "create-meal-plan",
                element: <CreateMealPlan />,
              },
              {
                path: "update-meal-plan/:id",
                element: <UpdateMealPlan />,
              },
            ],
          },
          {
            path: "financial-feedback",
            element: <FinancialFeedback />,
          },
          {
            path: "os-dashboard",
            element: <OSDashboard />,
            children: [
              {
                path: "create-new",
                element: <CreateBeneficiary />,
              },
            ],
          },
          {
            path: "qualitative-assessments",
            element: <QualitativeAssessments />,
          },
          {
            path: "qualitative-assessments-latest",
            element: <QualitativeAssessmentLatest />,
            children: [
              {
                path: "scope-of-all-programs",
                element: <ScopeOfPrograms />,
              },
            ],
          },
          {
            path: "manage-assessments",
            element: <ManageQAssessments />,
          },
          {
            path: "trustees-dashboard",
            element: <SMTDashboard />,
          },
          {
            path: "global-framework",
            element: <GlobalResultsFramework />,
          },
          {
            path: "france-framework",
            element: <FranceFrameWork />,
          },
          {
            path: "department-activities",
            element: <DepartmentActivities />,
          },
          {
            path: "HIPD-activities",
            element: <HIPDActivities />,
          },
          {
            path: "GD-activities",
            element: <GDActivities />,
          },
          {
            path: "uk-programs",
            element: <ProgramActivities />,
          },
          {
            path: "global-operations",
            element: <GlobalOperations />,
          },
          {
            path: "france-results-framework",
            element: <FranceResultsFramework />,
          },
          {
            path: "feedback",
            element: <FetchFeedBack />,
          },
          // {
          //   path: "bmis",
          //   element: <BMIS />,
          // },
        ],
      },
      {
        element: (
          <RequireAuthGuard>
            <ProcurementLayout />
          </RequireAuthGuard>
        ),
        children: [
          {
            path: "procurement",
            element: <Navigate to="/procurement/dashboard" replace />,
          },
          {
            path: "procurement/dashboard",
            element: <ProcurementOverview />,
          },
          {
            path: "procurement/purchase-requisitions",
            element: <PurchaseRequisitionDashboard />,
          },
          {
            path: "procurement/purchase-requisitions/add-requisitions",
            element: <PurchaseRequisition />,
          },
          {
            path: "procurement/supplier-registrations",
            element: <SupplierRegistrationDashboard />,
          },
          {
            path: "procurement/supplier-registration/add-supplier-registrations",
            element: <SupplierRegistration />,
          },
        ],
      },
      // Authenticated routes - Org Health App section
      {
        element: (
          <RequireAuthGuard>
            <OrgHealthLayout />
          </RequireAuthGuard>
        ),
        children: [
          // {
          //   path: "due-diligence",
          //   element: <DueDiligence />,
          // },
          {
            path: "project-management",
            element: <ProjectManagement />,
          },
          {
            path: "organization-management",
            element: <OrganizationManagement />,
          },
          {
            path: "human-resource-management",
            element: <HumanResourceManagement />,
          },
          {
            path: "financial-management",
            element: <FinancialManagement />,
          },
          {
            path: "administration",
            element: <Administration />,
          },
          {
            path: "governance-management",
            element: <GovernanceManagement />,
          },
          {
            path: "project-management/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "organization-management/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "human-resource-management/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "financial-management/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "administration/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "governance-management/comparison",
            element: <ComparisonPage />,
          },
          {
            path: "overall-comparison",
            element: <OverallComparisonPage />,
          },
        ],
      },
      // 404 Catch-all route - MUST BE LAST
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
